
// GoogleMapPicker.jsx - With Current Location Button
import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaTimes, FaSearch, FaSpinner, FaCrosshairs } from 'react-icons/fa';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDRcDLmtI8sk6pFB3w1o1N0RyPnRYO2HXQ';

const GoogleMapPicker = ({
    onLocationSelect,
    onClose,
    initialLat = 17.3850,
    initialLng = 78.4867,
    initialAddress = ''
}) => {
    const [map, setMap] = useState(null);
    const [marker, setMarker] = useState(null);
    const [searchQuery, setSearchQuery] = useState(initialAddress || '');
    const [selectedAddress, setSelectedAddress] = useState(initialAddress || '');
    const [selectedLat, setSelectedLat] = useState(initialLat);
    const [selectedLng, setSelectedLng] = useState(initialLng);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [gettingLocation, setGettingLocation] = useState(false);
    const [currentLocationError, setCurrentLocationError] = useState(null);
    const mapRef = useRef(null);
    const searchBoxRef = useRef(null);
    const autocompleteRef = useRef(null);

    // Load Google Maps script
    useEffect(() => {
        loadGoogleMaps();
    }, []);

    // Try to get current location when component mounts
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    ////console.log('📍 Current location detected:', latitude, longitude);
                    // Update initial location
                    setSelectedLat(latitude);
                    setSelectedLng(longitude);
                    if (map) {
                        map.setCenter({ lat: latitude, lng: longitude });
                        updateMarkerPosition(latitude, longitude);
                        reverseGeocode(latitude, longitude);
                        map.setZoom(16);
                    }
                },
                (error) => {
                    console.warn('Could not get current location:', error.message);
                    setCurrentLocationError('Could not get your location. Please search or click on the map.');
                }
            );
        }
    }, [map]);

    const loadGoogleMaps = () => {
        if (window.google && window.google.maps) {
            //console.log('✅ Google Maps already loaded');
            initializeMap();
            return;
        }

        setLoading(true);
        //console.log('🔄 Loading Google Maps script...');

        const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
        if (existingScript) {
            //console.log('⏳ Script already loading, waiting...');
            const checkLoaded = setInterval(() => {
                if (window.google && window.google.maps) {
                    clearInterval(checkLoaded);
                    //console.log('✅ Google Maps loaded from existing script');
                    initializeMap();
                }
            }, 500);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;

        window.initGoogleMap = () => {
            //console.log('✅ Google Maps script loaded via callback');
            initializeMap();
        };

        script.onerror = () => {
            console.error('❌ Failed to load Google Maps script');
            setLoading(false);
            alert('Failed to load Google Maps. Please check your internet connection and API key.');
        };

        document.head.appendChild(script);
    };

    const initializeMap = () => {
        try {
            if (!window.google || !window.google.maps) {
                console.error('❌ Google Maps not available');
                setLoading(false);
                return;
            }

            if (!mapRef.current) {
                console.error('❌ Map container not found');
                setLoading(false);
                return;
            }

            //console.log('🗺️ Initializing map at:', initialLat, initialLng);

            const mapOptions = {
                center: { lat: initialLat, lng: initialLng },
                zoom: 15,
                mapTypeControl: true,
                streetViewControl: true,
                fullscreenControl: true,
                zoomControl: true,
                mapTypeControlOptions: {
                    position: window.google.maps.ControlPosition.TOP_RIGHT,
                },
            };

            const mapInstance = new window.google.maps.Map(mapRef.current, mapOptions);
            setMap(mapInstance);
            setMapLoaded(true);
            setLoading(false);

            // Create initial marker
            const markerInstance = new window.google.maps.Marker({
                position: { lat: initialLat, lng: initialLng },
                map: mapInstance,
                draggable: true,
                animation: window.google.maps.Animation.DROP,
            });
            setMarker(markerInstance);

            // If initial address is provided, try to get coordinates
            if (initialAddress) {
                reverseGeocode(initialLat, initialLng);
            }

            // Click listener to place marker
            mapInstance.addListener('click', (e) => {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                updateMarkerPosition(lat, lng);
                reverseGeocode(lat, lng);
            });

            // Marker drag listener
            markerInstance.addListener('dragend', (e) => {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                reverseGeocode(lat, lng);
            });

            // Initialize search box
            if (searchBoxRef.current) {
                const autocompleteInstance = new window.google.maps.places.Autocomplete(searchBoxRef.current, {
                    types: ['geocode'],
                    componentRestrictions: { country: 'IN' },
                });
                autocompleteRef.current = autocompleteInstance;

                autocompleteInstance.addListener('place_changed', () => {
                    const place = autocompleteInstance.getPlace();
                    if (place.geometry) {
                        const lat = place.geometry.location.lat();
                        const lng = place.geometry.location.lng();
                        updateMarkerPosition(lat, lng);
                        const address = place.formatted_address || place.name || '';
                        setSelectedAddress(address);
                        setSearchQuery(address);
                        mapInstance.setCenter({ lat, lng });
                        mapInstance.setZoom(17);
                    }
                });
            }

            ////console.log('✅ Map initialized successfully');

            // After map loads, try to get current location
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        ////console.log('📍 Current location detected:', latitude, longitude);
                        setSelectedLat(latitude);
                        setSelectedLng(longitude);
                        mapInstance.setCenter({ lat: latitude, lng: longitude });
                        updateMarkerPosition(latitude, longitude);
                        reverseGeocode(latitude, longitude);
                        mapInstance.setZoom(16);
                    },
                    (error) => {
                        console.warn('Could not get current location:', error.message);
                        setCurrentLocationError('Could not get your location. Please search or click on the map.');
                    }
                );
            }

        } catch (error) {
            console.error('❌ Error initializing map:', error);
            setLoading(false);
            alert('Error loading map. Please try again.');
        }
    };

    const updateMarkerPosition = (lat, lng) => {
        if (marker) {
            marker.setPosition({ lat, lng });
        }
        setSelectedLat(lat);
        setSelectedLng(lng);
    };

    const reverseGeocode = (lat, lng) => {
        try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const address = results[0].formatted_address;
                    const components = results[0].address_components;

                    // ✅ Extract components correctly
                    let city = '';
                    let state = '';
                    let pincode = '';
                    let country = '';

                    components.forEach(component => {
                        const types = component.types;

                        // City: look for locality or administrative_area_level_2
                        if (types.includes('locality') || types.includes('administrative_area_level_2')) {
                            city = component.long_name;
                        }

                        // State: look for administrative_area_level_1
                        if (types.includes('administrative_area_level_1')) {
                            state = component.long_name;
                        }

                        // ✅ Pincode: look for postal_code
                        if (types.includes('postal_code')) {
                            pincode = component.long_name;  // This should be "500072"
                        }

                        // Country
                        if (types.includes('country')) {
                            country = component.long_name;
                        }
                    });

                    setSelectedAddress(address);
                    setSearchQuery(address);

                    // ✅ Debug: Log extracted values
                    //console.log('📍 Extracted Address Components:');
                    //console.log('  - City:', city);
                    //console.log('  - State:', state);
                    //console.log('  - Pincode:', pincode);
                    //console.log('  - Country:', country);

                    // ✅ Pass to parent
                    if (onLocationSelect) {
                        onLocationSelect({
                            latitude: lat,
                            longitude: lng,
                            address: address,
                            city: city,
                            state: state,
                            pincode: pincode,
                            country: country
                        });
                    }
                } else {
                    console.warn('Geocoding failed:', status);
                }
            });
        } catch (error) {
            console.error('Geocoding error:', error);
        }
    };

    const handleConfirm = () => {
        if (selectedLat && selectedLng && selectedAddress) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: selectedLat, lng: selectedLng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const components = results[0].address_components;
                    let city = '', state = '', pincode = '', country = '';

                    components.forEach(component => {
                        const types = component.types;
                        if (types.includes('locality') || types.includes('administrative_area_level_2')) {
                            city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            state = component.long_name;
                        }
                        if (types.includes('postal_code')) {
                            pincode = component.long_name;  // ✅ This will be "500072"
                        }
                        if (types.includes('country')) {
                            country = component.long_name;
                        }
                    });

                    // ✅ Debug
                    //console.log('📍 Confirming location:');
                    //console.log('  - Pincode:', pincode);

                    onLocationSelect({
                        latitude: selectedLat,
                        longitude: selectedLng,
                        address: selectedAddress,
                        city: city,
                        state: state,
                        pincode: pincode,
                        country: country
                    });
                } else {
                    onLocationSelect({
                        latitude: selectedLat,
                        longitude: selectedLng,
                        address: selectedAddress,
                    });
                }
                onClose();
            });
        } else {
            alert('Please select a location on the map');
        }
    };

    const handleSearch = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place && place.geometry) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                updateMarkerPosition(lat, lng);
                const address = place.formatted_address || place.name || '';
                setSelectedAddress(address);
                setSearchQuery(address);
                if (map) {
                    map.setCenter({ lat, lng });
                    map.setZoom(17);
                }
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    // Get current location button handler
    const handleGetCurrentLocation = () => {
        setGettingLocation(true);
        setCurrentLocationError(null);

        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            setGettingLocation(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                //console.log('📍 Current location fetched:', latitude, longitude);

                setSelectedLat(latitude);
                setSelectedLng(longitude);

                if (map) {
                    map.setCenter({ lat: latitude, lng: longitude });
                    updateMarkerPosition(latitude, longitude);
                    reverseGeocode(latitude, longitude);
                    map.setZoom(16);
                }

                setGettingLocation(false);
            },
            (error) => {
                console.error('❌ Error getting location:', error);
                setCurrentLocationError('Unable to get your location. Please check your browser permissions.');
                setGettingLocation(false);

                // Show user-friendly error message
                let errorMessage = 'Could not get your location. ';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage += 'Please allow location access in your browser settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage += 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage += 'The request to get your location timed out.';
                        break;
                    default:
                        errorMessage += 'Please try again or search manually.';
                }
                alert(errorMessage);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    return (
        <div style={mapModalOverlay} onClick={onClose}>
            <div style={mapModalStyle} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={mapModalHeader}>
                    <div style={mapModalHeaderLeft}>
                        <FaMapMarkerAlt size={18} style={{ color: '#e66d33' }} />
                        <h3 style={mapModalTitle}>Select Location on Map</h3>
                    </div>
                    <button onClick={onClose} style={mapModalClose}>
                        <FaTimes />
                    </button>
                </div>

                {/* Search Box */}
                <div style={mapSearchContainer}>
                    <div style={mapSearchWrapper}>
                        <FaSearch style={mapSearchIcon} />
                        <input
                            ref={searchBoxRef}
                            type="text"
                            placeholder="Search for a location..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={mapSearchInput}
                            onKeyPress={handleKeyPress}
                        />
                    </div>
                    <button
                        onClick={handleGetCurrentLocation}
                        style={currentLocationButtonStyle}
                        disabled={gettingLocation}
                        title="Go to your current location"
                    >
                        {gettingLocation ? (
                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={16} />
                        ) : (
                            <FaCrosshairs size={16} />
                        )}
                        <span style={{ marginLeft: '6px', fontSize: '13px' }}>
                            {gettingLocation ? 'Getting...' : 'Current Location'}
                        </span>
                    </button>
                </div>

                {/* Map Container with Loading State */}
                <div style={mapContainerWrapper}>
                    {loading && (
                        <div style={mapLoadingOverlay}>
                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={40} color="#e66d33" />
                            <p style={{ marginTop: '12px', color: '#6B7280' }}>Loading map...</p>
                        </div>
                    )}
                    <div
                        ref={mapRef}
                        style={{
                            ...mapContainer,
                            display: loading ? 'none' : 'block'
                        }}
                    />
                    {currentLocationError && !loading && (
                        <div style={mapErrorOverlay}>
                            <span style={{ color: '#DC2626', fontSize: '13px' }}>⚠️ {currentLocationError}</span>
                        </div>
                    )}
                </div>

                {/* Selected Location Info */}
                <div style={mapInfoContainer}>
                    <div style={mapInfoRow}>
                        <span style={mapInfoLabel}>📍 Address:</span>
                        <span style={mapInfoValue}>{selectedAddress || 'Click on map to select location'}</span>
                    </div>
                    <div style={mapInfoRow}>
                        <span style={mapInfoLabel}>📍 Coordinates:</span>
                        <span style={mapInfoValue}>
                            {selectedLat.toFixed(6)}, {selectedLng.toFixed(6)}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                <div style={mapActionsContainer}>
                    <button onClick={onClose} style={mapCancelButton}>
                        Cancel
                    </button>
                    <button onClick={handleConfirm} style={mapConfirmButton}>
                        <FaMapMarkerAlt size={14} />
                        Confirm Location
                    </button>
                </div>

                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </div>
    );
};

// Styles for the map picker
const mapModalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100000,
    padding: '20px',
};

const mapModalStyle = {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    overflow: 'auto',
    scrollbarWidth: "thin",
};

const mapModalHeader = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #F3F4F6',
    flexShrink: 0,
};

const mapModalHeaderLeft = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
};

const mapModalTitle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
};

const mapModalClose = {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6B7280',
    padding: '4px 8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
};

const mapSearchContainer = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 24px',
    borderBottom: '1px solid #F3F4F6',
    flexShrink: 0,
};

const mapSearchWrapper = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    background: '#fff',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    padding: '0 14px',
    transition: 'all 0.2s ease',
};

const mapSearchIcon = {
    color: '#9CA3AF',
    marginRight: '10px',
};

const mapSearchInput = {
    flex: 1,
    padding: '10px 0',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    color: '#111827',
};

const currentLocationButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    background: '#e66d33',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    flexShrink: 0,
};

const mapContainerWrapper = {
    position: 'relative',
    width: '100%',
    height: '400px',
    flexShrink: 0,
    background: '#f3f4f6',
};

const mapContainer = {
    width: '100%',
    height: '100%',
};

const mapLoadingOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f9fafb',
    zIndex: 10,
};

const mapErrorOverlay = {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '8px 16px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 10,
    maxWidth: '90%',
    textAlign: 'center',
};

const mapInfoContainer = {
    padding: '12px 24px',
    background: '#F9FAFB',
    borderTop: '1px solid #F3F4F6',
    flexShrink: 0,
};

const mapInfoRow = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    fontSize: '13px',
};

const mapInfoLabel = {
    fontWeight: 600,
    color: '#374151',
    minWidth: '80px',
};

const mapInfoValue = {
    color: '#6B7280',
    wordBreak: 'break-all',
};

const mapActionsContainer = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #F3F4F6',
    flexShrink: 0,
};

const mapCancelButton = {
    padding: '10px 24px',
    background: '#F3F4F6',
    color: '#4B5563',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const mapConfirmButton = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 28px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(230, 109, 51, 0.3)',
};

export default GoogleMapPicker;